import { Fragment, useCallback, useMemo, useRef } from 'react'
import isEmpty from 'lodash/isEmpty'
import { useImmer } from 'use-immer'
import { useSelector } from 'react-redux'
import shuffle from 'lodash/shuffle'
//
import { selectAdInfo } from 'redux/selector/app'
//
import {
  fetchComicList,
  fetchComicTotalQty,
  fetchShortList,
  fetchShortTotalQty,
  fetchVideoList,
  fetchVideoTotalQty,
  type SchemaComicShow,
  type SchemaShortVideoShow,
  type SchemaVideoShow,
} from 'api'
//
import { AdvertisementType } from 'constant/advertisement'
//
import HeaderBox from 'component/PageModalHeaderBox'
import VideoBox from 'component/VideoBox'
import VideoBoxVertical from 'component/VideoBoxVertical'
import EmptyResult from 'component/EmptyResult'
import AdItem from './component/AdItem'
//
import ComicBox from 'component/ComicBox'
import {
  AreaPanelCmsGridWrapper,
  HeaderWrap,
  InfiniteScrollWrapper,
  PageScrollWrap,
  PageWrapperNonScroll,
} from 'commonStyledComponents'
import { useParamsOfPage } from 'hooks/useNavigateTo'
import PullToRefresh from 'react-simple-pull-to-refresh'
import PagePullLoad from 'component/Loading/PagePullLoad'

interface ViewMoreState {
  nextPage: number
  isLoading: boolean
  hasMore: boolean
  list: ViewMoreItem[]
  page_result: {
    current: number | null
    pageSize: number | null
    total: number | null
  }
  loadedPages: number[]
}

type ViewMoreItem = SchemaVideoShow | SchemaShortVideoShow | SchemaComicShow

export default function ViewMore() {
  const paramsOfPage = useParamsOfPage('view-more')
  const {
    type = 'video',
    params,
    headerTitle: headerTitleProp,
    showAd = true,
  } = paramsOfPage as NonNullable<typeof paramsOfPage>

  const adInfo = useSelector(selectAdInfo)
  const adViewMore = useMemo(() => {
    return adInfo?.[AdvertisementType.ViewMore]
  }, [adInfo])
  // 是否需要亂數排序
  const randomSort = useMemo(() => {
    return params.random
  }, [params])

  const [state, updateState] = useImmer<ViewMoreState>({
    nextPage: 1,
    isLoading: true,
    hasMore: true,
    list: [],
    page_result: { current: null, pageSize: null, total: null },
    loadedPages: [], // loaded pages index array
  })
  const loading = useRef(false)

  const headerTitle = headerTitleProp || '看更多'

  const itemPerRow =
    type === 'video'
      ? params?.isVerticalVideoBox
        ? 2
        : params?.isLargeVideoBox
        ? 1
        : 2
      : type === 'short'
      ? 2
      : type === 'comic'
      ? 3
      : 2 // as default

  const handleFetchVideoList = useCallback(async () => {
    if (!state.hasMore) return

    const data: any = {
      // current: state.nextPage,
      pageSize: 30,
      ...params,
    }

    if (loading.current) return
    loading.current = true
    let total = state?.page_result?.total
    if (!total) {
      // update total
      const totalRes =
        type === 'short'
          ? await fetchShortTotalQty({ ...params } as Parameters<typeof fetchShortTotalQty>[0])
          : type === 'comic'
          ? await fetchComicTotalQty({ ...params } as Parameters<typeof fetchComicTotalQty>[0])
          : await fetchVideoTotalQty({ ...params } as Parameters<typeof fetchVideoTotalQty>[0])
      total = totalRes?.page_result?.total || 0
      if (totalRes?.page_result) {
        updateState((draft) => {
          draft.page_result = totalRes?.page_result
        })
      }
    }

    if (total) {
      // random page
      const pages = Math.ceil(total / data.pageSize)
      const numbers = Array.from({ length: pages }, (m, i) => i + 1)
      let next = state.nextPage
      let hasMore = false
      if (randomSort) {
        const filteredNumbers = numbers.filter((m) => !state.loadedPages.includes(m))
        next = filteredNumbers[Math.floor(Math.random() * filteredNumbers.length)]
        hasMore = filteredNumbers.length > 1
      }
      data.current = next
      const response =
        type === 'short'
          ? await fetchShortList(data as Parameters<typeof fetchShortList>[0])
          : type === 'comic'
          ? await fetchComicList(data as Parameters<typeof fetchComicList>[0])
          : await fetchVideoList(data as Parameters<typeof fetchVideoList>[0])
      let items: ViewMoreItem[] = response?.data ?? []
      if (randomSort) {
        items = shuffle(items)
      } else {
        hasMore = response?.page_result?.total > response?.page_result?.current * response?.page_result?.pageSize
      }
      updateState((draft) => {
        if (draft.loadedPages.includes(next)) return
        draft.list = draft?.list?.concat(items)
        draft.hasMore = hasMore
        draft.nextPage = (response?.page_result?.current ?? 0) + 1
        draft.page_result = response.page_result as ViewMoreState['page_result']
        draft.isLoading = false
        draft.loadedPages = [...draft.loadedPages, next]
      })
      loading.current = false
    } else {
      updateState((draft) => {
        draft.list = []
        draft.hasMore = false
        draft.nextPage = 1
        draft.isLoading = false
        draft.loadedPages = []
      })
    }
  }, [params, randomSort, state, type, updateState])

  const handleRefresh = useCallback(async () => {
    // load event will be triggered after update state
    updateState((draft) => {
      draft.list = []
      draft.hasMore = true
      draft.nextPage = 1
      draft.page_result = { current: null, pageSize: null, total: null }
      draft.isLoading = true
      draft.loadedPages = []
    })
  }, [updateState])

  const shortInfo = useMemo(() => {
    return {
      list: state.list as object[],
    }
  }, [state.list])

  return (
    <PageWrapperNonScroll>
      <HeaderWrap>
        <HeaderBox headerTitle={headerTitle} />
      </HeaderWrap>
      {!state?.list?.length && !state.isLoading ? (
        <EmptyResult />
      ) : (
        <PageScrollWrap className="lazy-root">
          <PullToRefresh
            onRefresh={handleRefresh}
            pullingContent={<PagePullLoad paused />}
            refreshingContent={<PagePullLoad />}
          >
            <InfiniteScrollWrapper
              pageStart={0}
              loadMore={handleFetchVideoList}
              hasMore={state?.hasMore}
              // loader={<div className="loader" key={0}>Loading ...</div>}
              useWindow={false}
            >
              {state?.list?.length > 0 && (
                <AreaPanelCmsGridWrapper>
                  {state?.list?.map((item, i) => {
                    /** 每四行 放置一個 視頻櫥窗更多廣告位 */
                    const itemsPerAd = itemPerRow * 4
                    const isPutAdItem = showAd ? (i + 1) % itemsPerAd === 0 : false
                    const adIndex = ((i + 1) / itemsPerAd) % adViewMore?.length
                    const adItemInfo = adViewMore?.[adIndex]

                    return (
                      <Fragment key={i}>
                        {type === 'video' &&
                          (params?.isVerticalVideoBox ? (
                            <VideoBoxVertical columns={itemPerRow} videoInfo={item as SchemaShortVideoShow} />
                          ) : (
                            <VideoBox columns={itemPerRow} videoInfo={item as SchemaVideoShow} />
                          ))}
                        {type === 'short' && (
                          <VideoBoxVertical
                            columns={itemPerRow}
                            videoInfo={item as SchemaShortVideoShow}
                            shortInfo={shortInfo}
                          />
                        )}
                        {type === 'comic' && <ComicBox columns={itemPerRow} comicInfo={item as SchemaComicShow} />}
                        {isPutAdItem && !isEmpty(adItemInfo) && <AdItem {...adItemInfo} />}
                      </Fragment>
                    )
                  })}
                </AreaPanelCmsGridWrapper>
              )}
            </InfiniteScrollWrapper>
          </PullToRefresh>
        </PageScrollWrap>
      )}
    </PageWrapperNonScroll>
  )
}
